/**
 * HS 관세율 코드 분류 taxonomy — frontend mirror of backend/ontology/rateCodeTaxonomy.js
 *
 * categorizeRate(code, label) → { category, isFta, ftaRegion, ftaPartner, selectNo }
 * groupRatesForDisplay(ratesFull) → { basic, general, fta }
 *
 * 코드 변경 시 backend 와 동기 필요.
 */

// ─── categorizeRate ────────────────────────────────────────────────────────────

export function categorizeRate(code, label) {
  if (!code) return _misc();

  const c = String(code).trim();

  // A: 기본세율
  if (c === "A" || /^A\d/.test(c)) {
    return {
      category: "basic",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // B: 잠정세율
  if (/^B/.test(c)) {
    return {
      category: "provisional",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // C: WTO (C, C1-C6, C2A*)
  if (c === "C" || /^C\d/.test(c) || /^C2A/.test(c)) {
    return {
      category: "wto",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // D: WTO개도국
  if (c === "D") {
    return {
      category: "wto",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // E: APTA (E1, E2, E3, E1A*, E1B*, E2A*, E3A*)
  if (/^E[123]/.test(c)) {
    return {
      category: "apta",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // F (single char): 국제협력관세
  if (c === "F") {
    return {
      category: "intl_coop",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // F...: FTA
  if (c.startsWith("F") && c.length > 1) {
    return _ftaCategory(c, label);
  }

  // G: UN개도국
  if (/^G/.test(c)) {
    return {
      category: "preference_gsp",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // I: 덤핑방지
  if (c === "I" || /^I\d/.test(c)) {
    return {
      category: "dumping",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // L: 조정관세
  if (c === "L" || /^L\d/.test(c)) {
    return {
      category: "adjustment",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // P: 할당관세
  if (/^P/.test(c)) {
    return {
      category: "quota_alloc",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // R: 최빈국특혜
  if (c === "R" || /^R\d/.test(c)) {
    return {
      category: "preference_ldc",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // T: 특별긴급관세
  if (/^T/.test(c)) {
    return {
      category: "safeguard",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // U: 북한산
  if (c === "U" || /^U\d/.test(c)) {
    return {
      category: "preference_nk",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // W: 농림축산물양허관세
  if (/^W/.test(c)) {
    return {
      category: "quota_agri",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // Y: 양허관련
  if (/^Y/.test(c)) {
    return {
      category: "wto_related",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  // Z: misc
  if (/^Z/.test(c)) {
    return {
      category: "misc",
      isFta: false,
      ftaRegion: null,
      ftaPartner: null,
      selectNo: null,
    };
  }

  return _misc();
}

function _misc() {
  return {
    category: "misc",
    isFta: false,
    ftaRegion: null,
    ftaPartner: null,
    selectNo: null,
  };
}

function _ftaCategory(code, label) {
  const lb = label || "";
  return {
    category: "fta",
    isFta: true,
    ftaRegion: _parseFtaRegion(code, lb),
    ftaPartner: _parseFtaPartner(code, lb),
    selectNo: _parseSelectNo(lb),
  };
}

function _parseSelectNo(label) {
  const m = label.match(/\(선택(\d+)\)/);
  return m ? parseInt(m[1], 10) : null;
}

function _parseFtaRegion(code, label) {
  const lb = label || "";
  const c = code || "";

  // RCEP 먼저 (아세안/중국/호주 등이 RCEP label 에도 포함)
  if (/RCEP/.test(lb)) return "rcep";
  if (/EU|EFTA|영국/.test(lb)) return "europe";
  if (/미\s*FTA|미국|캐나다|중미|콜롬비아|페루|칠레/.test(lb)) return "america";
  if (
    /중국|인도|아세안|베트남|캄보디아|필리핀|싱가포르|인도네시아|호주|뉴질랜드|상호대응/.test(
      lb,
    )
  )
    return "asia";
  if (/터키|이스라엘|UAE|아랍에미리트/.test(lb)) return "middle";

  // code-based fallback
  if (/^FRC/.test(c)) return "rcep";
  if (/^FEU/.test(c) || /^FGB/.test(c) || /^FEF/.test(c)) return "europe";
  if (
    /^FUS/.test(c) ||
    /^FCA/.test(c) ||
    /^FCL/.test(c) ||
    /^FCO/.test(c) ||
    /^FPE/.test(c) ||
    /^FCE/.test(c)
  )
    return "america";
  if (
    /^FCN/.test(c) ||
    /^FIN/.test(c) ||
    /^FAS/.test(c) ||
    /^FVN/.test(c) ||
    /^FKH/.test(c) ||
    /^FPH/.test(c) ||
    /^FSG/.test(c) ||
    /^FID/.test(c) ||
    /^FAU/.test(c) ||
    /^FNZ/.test(c)
  )
    return "asia";
  if (/^FTR/.test(c) || /^FIL/.test(c) || /^FAE/.test(c)) return "middle";

  return "other";
}

function _parseFtaPartner(code, label) {
  const lb = label || "";

  // RCEP sub-country
  const rcepM = lb.match(/RCEP협정세율_(.+?)(?:\(선택|$)/);
  if (rcepM) return `RCEP${rcepM[1].trim()}`;

  // 상호대응
  if (/상호대응세율/.test(lb)) {
    const m2 = lb.match(/상호대응세율\((.+?)\)/);
    return m2 ? `아세안_${m2[1]}` : "아세안상호대응";
  }

  // 한ㆍX FTA/CEPA/협정 — use \bFTA\b to avoid capturing "E" from "EFTA FTA"
  const m = lb.match(/한[ㆍ·](.+?)(?:\s+\bFTA\b|CEPA|협정)/);
  if (m) return m[1].replace(/\s/g, "").trim();

  // code fallback
  if (/^FEU/.test(code)) return "EU";
  if (/^FGB/.test(code)) return "영국";
  if (/^FEF/.test(code)) return "EFTA";
  if (/^FUS/.test(code)) return "미";
  if (/^FCN/.test(code)) return "중국";
  if (/^FAE/.test(code)) return "UAE";
  return null;
}

// ─── groupRatesForDisplay ──────────────────────────────────────────────────────

const GENERAL_CATEGORY_ORDER = [
  "wto",
  "apta",
  "quota_alloc",
  "quota_agri",
  "safeguard",
  "dumping",
  "adjustment",
  "intl_coop",
  "preference_ldc",
  "preference_nk",
  "preference_gsp",
  "wto_related",
  "misc",
];

/**
 * ratesFull: { [code]: { value, label, raw } }
 * Returns { basic, general, fta }
 */
export function groupRatesForDisplay(ratesFull) {
  if (!ratesFull || typeof ratesFull !== "object") {
    return {
      basic: null,
      general: [],
      fta: {
        europe: [],
        america: [],
        asia: [],
        middle: [],
        rcep: [],
        other: [],
      },
    };
  }

  let basic = null;
  const general = [];
  const fta = {
    europe: [],
    america: [],
    asia: [],
    middle: [],
    rcep: [],
    other: [],
  };

  for (const [code, entry] of Object.entries(ratesFull)) {
    if (!entry || typeof entry !== "object") continue;
    const { value, label, raw } = entry;
    const info = categorizeRate(code, label);

    if (info.category === "basic") {
      if (code === "A") {
        basic = {
          code,
          value: value ?? null,
          raw: raw || null,
          label: label || code,
        };
      } else {
        // A1, A2 etc — treat as general wto bucket
        general.push({
          code,
          value: value ?? null,
          raw: raw || null,
          label: label || code,
          category: "wto",
        });
      }
      continue;
    }

    if (info.isFta) {
      const region = info.ftaRegion || "other";
      const bucket = fta[region] || fta.other;
      bucket.push({
        code,
        value: value ?? null,
        raw: raw || null,
        label: label || code,
        partner: info.ftaPartner || null,
        selectNo: info.selectNo,
      });
      continue;
    }

    general.push({
      code,
      value: value ?? null,
      raw: raw || null,
      label: label || code,
      category: info.category,
    });
  }

  // Sort general
  general.sort((a, b) => {
    const oa = GENERAL_CATEGORY_ORDER.indexOf(a.category);
    const ob = GENERAL_CATEGORY_ORDER.indexOf(b.category);
    const orderA = oa === -1 ? 99 : oa;
    const orderB = ob === -1 ? 99 : ob;
    if (orderA !== orderB) return orderA - orderB;
    return String(a.code).localeCompare(String(b.code));
  });

  // Sort FTA buckets
  for (const bucket of Object.values(fta)) {
    bucket.sort((a, b) => {
      const pa = a.partner || "";
      const pb = b.partner || "";
      if (pa !== pb) return pa.localeCompare(pb);
      return (a.selectNo || 0) - (b.selectNo || 0);
    });
  }

  return { basic, general, fta };
}
